/**
 * Utility functions and helpers for ProxyEditPanel component.
 * Extracted to reduce cognitive complexity of the main component.
 */

import type { CurrentProxyRecord } from '../../../../store/slices/admin/requestorProxySlice'
import type { AvailableEmployeeRecord } from '../../../../store/slices/admin/approverProxySlice'

/** Minimum characters required for the employee last-name search (per searchAvailableEmployees contract). */
export const EMPLOYEE_LAST_NAME_MIN_LENGTH = 2

/** Allows only alphanumeric characters and spaces in name search fields. */
export const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9 ]*$/

/** Maps Oracle 3-character month abbreviations to 2-digit month numbers. */
export const ORACLE_MONTH_MAP: Record<string, string> = {
  JAN: '01',
  FEB: '02',
  MAR: '03',
  APR: '04',
  MAY: '05',
  JUN: '06',
  JUL: '07',
  AUG: '08',
  SEP: '09',
  OCT: '10',
  NOV: '11',
  DEC: '12',
}

/**
 * Unified person model shared by both edit-panel tables so a single record can
 * move between the "Approver Group" table and the "Search Results" table.
 */
export interface EditablePerson {
  /** Stable unique id (NUID or email) — used as the React key and move lookup key. */
  id: string
  /** Display name, e.g. "Nunez, Richard Cruz (B302082)". */
  name: string
  /** Geographic region. */
  region: string
  /** Department name. */
  department: string
  /** Email address. */
  email: string
  /** Proxy start date; only set for current-proxy records. */
  startDate?: string
  /** Proxy end date; only set for current-proxy records. */
  endDate?: string
  /**
   * True when this person was added from search results in the current session.
   * False (or absent) when the person was loaded from the fetchCurrent API.
   * Used to populate `existingApproverProxy` in the assign payload.
   */
  isNewlyAdded?: boolean
}

/** Trimmed edit-panel search field values collected before validation. */
export interface TrimmedEmployeeSearch {
  firstName: string
  lastName: string
  nuid: string
}

/**
 * Converts any of the four API date formats to MM/DD/YYYY for the Datepicker.
 *
 * Handled inputs:
 *   - `YYYY-MM-DD`  (ISO 8601)       → re-orders to MM/DD/YYYY
 *   - `MM-DD-YYYY`  (dash-sep)       → replaces dashes with slashes
 *   - `MM/DD/YYYY`  (already correct) → returned as-is
 *   - `DD-MON-YY`   (Oracle format)  → e.g. "01-JAN-26" → "01/01/2026"
 *   - `DD-MON-YYYY` (Oracle full yr) → e.g. "01-JAN-2026" → "01/01/2026"
 */
export const toMMDDYYYY = (dateStr: string): string => {
  if (!dateStr) return ''
  // Already in MM/DD/YYYY (or any slash-delimited format) — return as-is
  if (dateStr.includes('/')) return dateStr
  const parts = dateStr.split('-')
  if (parts.length !== 3) return ''
  // Oracle DD-MON-YY or DD-MON-YYYY: middle segment is a month abbreviation
  const oracleMM = ORACLE_MONTH_MAP[parts[1].toUpperCase()]
  if (oracleMM) {
    const yy = parts[2].length === 2 ? `20${parts[2]}` : parts[2]
    return `${oracleMM}/${parts[0].padStart(2, '0')}/${yy}`
  }
  // ISO YYYY-MM-DD: year is always 4 digits in the first segment
  if (parts[0].length === 4) return `${parts[1]}/${parts[2]}/${parts[0]}`
  // MM-DD-YYYY: just swap delimiters
  return `${parts[0]}/${parts[1]}/${parts[2]}`
}

/** Returns today's date as MM/DD/YYYY. */
export const todayMMDDYYYY = (): string => {
  const t = new Date()
  return `${String(t.getMonth() + 1).padStart(2, '0')}/${String(t.getDate()).padStart(2, '0')}/${t.getFullYear()}`
}

/** Parses a MM/DD/YYYY string into a Date for chronological comparison. */
export const parseMMDDYYYY = (date: string): Date => {
  const [m, d, y] = date.split('/').map(Number)
  return new Date(y, m - 1, d)
}

/** Adds years to a MM/DD/YYYY date string. */
export const addYears = (dateStr: string, years: number): string => {
  const date = parseMMDDYYYY(dateStr)
  date.setFullYear(date.getFullYear() + years)
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`
}

/** Adds days to a MM/DD/YYYY date string. */
export const addDays = (dateStr: string, days: number): string => {
  const date = parseMMDDYYYY(dateStr)
  date.setDate(date.getDate() + days)
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`
}

/** Calculate maximum end date (start + 10 years - 1 day). */
export const getMaxEndDate = (startDate: string): string =>
  addDays(addYears(startDate, 10), -1)

/**
 * Validates proxy date range: end date must be after start date and within 10 years.
 * @returns Error message or empty string if valid.
 */
export const validateProxyDates = (
  startDate: string,
  endDate: string
): string => {
  if (!startDate || !endDate) return ''

  const maxAllowedEnd = getMaxEndDate(startDate)

  if (parseMMDDYYYY(endDate) > parseMMDDYYYY(maxAllowedEnd)) {
    return 'End date cannot be more than 10 years from start date'
  }

  if (parseMMDDYYYY(endDate) <= parseMMDDYYYY(startDate)) {
    return 'End date must be after start date'
  }

  return ''
}

/**
 * Maps a fetched current-proxy record to the unified {@link EditablePerson}.
 *
 * @param proxy - A current proxy assignment (from fetchCurrentAP / fetchCurrentRP / fetchCurrentFE).
 * @returns The unified person model.
 */
export const toEditableFromProxy = (
  proxy: CurrentProxyRecord
): EditablePerson => ({
  id: proxy.value || proxy.label,
  name: proxy.label,
  region: proxy.region,
  department: proxy.department,
  // Normalize sentinel "?" values to '' so the UI can display "N/A" consistently.
  email: proxy.email && proxy.email !== '?' ? proxy.email : '',
  startDate: proxy.startDate ? toMMDDYYYY(proxy.startDate) : undefined,
  endDate: proxy.endDate ? toMMDDYYYY(proxy.endDate) : undefined,
  // Records loaded from the API are pre-existing assignments.
  isNewlyAdded: false,
})

/**
 * Maps a searchAvailableEmployees result to the unified {@link EditablePerson}.
 * Email values of `"?"` are normalised to an empty string so they do not act
 * as a stable ID or appear in save payloads. The employee's name (which
 * includes their NUID, e.g. "Russo, Kathleen E (A390634)") is used as the
 * stable unique ID so that multiple employees with missing email addresses
 * can coexist in the table without key collisions.
 *
 * @param emp - An eligible employee returned by the employee search.
 * @returns The unified person model.
 */
export const toEditableFromEmployee = (
  emp: AvailableEmployeeRecord
): EditablePerson => {
  // Treat "?" as no email — the API may return this sentinel for employees
  // whose email address is not yet populated in the directory.
  const normalizedEmail = emp.email === '?' ? '' : (emp.email ?? '')
  return {
    id: emp.name,
    name: emp.name,
    region: emp.region,
    department: emp.department,
    email: normalizedEmail,
  }
}

/**
 * Validates NUID search field.
 * @returns Validation errors map.
 */
export const validateNuidSearch = (nuid: string): Record<string, string> => {
  if (!nuid) {
    return { nuid: 'Please enter search key value' }
  }
  return {}
}

/**
 * Validates name search fields (firstName, lastName).
 * @returns Validation errors map.
 */
export const validateNameSearch = (
  trimmed: TrimmedEmployeeSearch
): Record<string, string> => {
  const errors: Record<string, string> = {}

  if (trimmed.firstName && !ALPHANUMERIC_REGEX.test(trimmed.firstName)) {
    errors.firstName =
      'Please only enter alphanumeric characters (0-9 a-z A-Z).'
  }

  if (trimmed.lastName && !ALPHANUMERIC_REGEX.test(trimmed.lastName)) {
    errors.lastName = 'Please only enter alphanumeric characters (0-9 a-z A-Z).'
  }

  if (!errors.lastName) {
    if (!trimmed.lastName) {
      errors.lastName = 'Please enter Last Name value for search'
    } else if (trimmed.lastName.length < EMPLOYEE_LAST_NAME_MIN_LENGTH) {
      errors.lastName = 'Last name must be at least 2 characters'
    }
  }

  return errors
}

/**
 * Builds validation errors for the current edit-panel search mode.
 * @param trimmed - Trimmed field values.
 * @param searchType - Current search type ('name' or 'nuid').
 * @returns A map of field → error message (empty when valid).
 */
export const buildEditValidationErrors = (
  trimmed: TrimmedEmployeeSearch,
  searchType: string
): Record<string, string> => {
  return searchType === 'nuid'
    ? validateNuidSearch(trimmed.nuid)
    : validateNameSearch(trimmed)
}

/**
 * Extracts NUID from formatted name string (e.g., "Last, First (NUID)").
 * @param name - Formatted name string.
 * @param fallbackId - Fallback ID if NUID cannot be extracted.
 * @returns Extracted NUID or fallback ID.
 */
export const extractNuidFromName = (
  name: string,
  fallbackId: string
): string => {
  const nuidRegex = /\(([^()]*)\)\s*$/
  return nuidRegex.exec(name)?.[1] ?? fallbackId
}

/**
 * Resolves the column label for the proxy assignment table based on active mode.
 * @param mode - Active proxy mode
 * @returns Header label string
 */
export const getProxyColumnLabel = (mode?: string): string => {
  if (mode === 'approver') return 'Selected Approver Proxy'
  if (mode === 'float') return 'Selected Float Employee'
  return 'Current Requestor Proxy'
}

/**
 * Builds table column definitions for the proxy assignment table based on mode.
 * @param mode - Active proxy mode
 * @param isDateEditableMode - Whether date fields are editable in this mode
 * @returns Column definition array
 */
export const buildApproverColumns = (
  mode: string,
  isDateEditableMode: boolean
) => [
  {
    key: 'proxy',
    label: getProxyColumnLabel(mode),
    className: 'md:!min-w-[180px]',
  },
  { key: 'region', label: 'Region', className: 'md:!min-w-[100px]' },
  { key: 'department', label: 'Department', className: 'md:!min-w-[140px]' },
  { key: 'startDate', label: 'Start Date', className: 'md:!min-w-[150px]' },
  ...(isDateEditableMode
    ? [{ key: 'endDate', label: 'End Date', className: 'md:!min-w-[150px]' }]
    : []),
  { key: 'email', label: 'Email', className: 'md:!min-w-[100px]' },
  { key: 'action', label: 'Remove', className: 'md:!min-w-[140px]' },
]

/** Column definitions for the employee search results table in ProxyEditPanel. */
export const EDIT_PANEL_RESULT_COLUMNS = [
  { key: 'empName', label: 'Employee', className: 'md:!min-w-[200px]' },
  { key: 'region', label: 'Region', className: 'md:!min-w-[100px]' },
  { key: 'department', label: 'Department', className: 'md:!min-w-[140px]' },
  { key: 'email', label: 'Email', className: 'md:!min-w-[160px]' },
  { key: 'action', label: 'Add', className: 'md:!min-w-[180px]' },
]
